import type { MeResponse } from '@xchange/shared';
import { useEffect, useState } from 'react';
import { RequestError } from '../api.js';
import type { Platform } from '../platform/index.js';
import { toast } from '../toast.js';
import {
  activeWalletId,
  createWallet,
  labelActiveWallet,
  listWallets,
  loadSealed,
  signInWithWallet,
  startAnotherWallet,
  switchWallet,
  type WalletEntry,
} from '../wallet.js';
import { Loading } from './Loading.js';
import { track, trackError } from '../track.js';
import { useWelcomeWatch } from '../welcomeWatch.js';
import { SharedLine, useSharedLine } from '../sharedLine.js';
import { PinPad } from './PinPad.js';
import { RestoreWords } from './WordsCheck.js';
import { BUY_USDC_WELCOME_LINE } from './BuyUsdc.js';
import { readDraft } from '../ticket.js';
import { headlineFor, useWelcomeMarket, WelcomeBook, WelcomeTicket } from './WelcomeMarket.js';

type Step =
  | 'checking'
  | 'pin' // a wallet is on this device: its PIN opens it and signs in
  | 'choose' // several wallets on this device: which one
  | 'welcome'
  | 'restore'
  | 'new-pin'
  | 'confirm'
  | 'working';

/**
 * Sign-in in a plain browser: the wallet on this device, behind its PIN, is
 * the account. No password and nothing to reset; the twelve words are the
 * way back in, as in Telegram. A new wallet is made here the same way, and
 * its words are shown and typed back before anything else.
 */
export function WebSignIn({
  platform,
  onSignedIn,
}: {
  platform: Platform;
  /** Signed in; a brand-new wallet also hands over its phrase, to be shown and typed back. */
  onSignedIn: (me: MeResponse, freshPhrase: string | null) => void;
}) {
  const [step, setStep] = useState<Step>('checking');
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [restorePhrase, setRestorePhrase] = useState('');
  const [busy, setBusy] = useState(false);
  // Said in place of the PIN hint after two PINs did not match.
  const [pinNote, setPinNote] = useState<string | null>(null);
  const [wallets, setWallets] = useState<WalletEntry[]>([]);
  const ref = new URLSearchParams(window.location.search).get('ref') ?? undefined;

  useEffect(() => {
    void loadSealed(platform)
      .catch(() => null)
      .then((sealed) => {
        setWallets(listWallets());
        setStep(sealed === null ? 'welcome' : 'pin');
        // Which first screen this device got, so the funnel can tell a
        // returning wallet at its PIN from a newcomer who never saw the welcome.
        track('start_screen', { is: sealed === null ? 'welcome' : 'pin' });
      });
  }, [platform]);
  const active = wallets.find((w) => w.id === activeWalletId());

  const fail = (e: unknown): void => {
    const message = e instanceof Error ? e.message : String(e);
    if (e instanceof RequestError && e.status >= 500) {
      toast.err('xchange did not answer. It may be restarting; try again in a moment.');
      return;
    }
    toast.err(
      message === 'Wrong PIN' || e instanceof RequestError
        ? message
        : `Could not sign in: ${message}`,
    );
  };

  // The wallet already here: PIN, sign the challenge, in.
  const unlock = (pin: string): void => {
    setBusy(true);
    signInWithWallet(platform, pin, ref)
      .then((me) => {
        track('signed_in', { how: 'pin' });
        labelActiveWallet(me.displayName);
        onSignedIn(me, null);
      })
      .catch((e: unknown) => {
        trackError('unlock');
        fail(e);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  // A wallet made or restored now, then the same sign-in.
  const create = async (pin: string): Promise<void> => {
    setStep('working');
    try {
      const made = await createWallet(
        platform,
        null,
        pin,
        restorePhrase === '' ? undefined : restorePhrase,
      );
      setRestorePhrase('');
      track(made.phrase === null ? 'restored' : 'pin_set');
      const me = await signInWithWallet(platform, pin, ref);
      track('signed_in', { how: made.phrase === null ? 'restore' : 'new' });
      labelActiveWallet(me.displayName);
      onSignedIn(me, made.phrase);
    } catch (e) {
      trackError('wallet_create');
      fail(e);
      setStep('welcome');
    }
  };

  if (step === 'checking' || step === 'working') {
    return (
      <Loading text={step === 'working' ? 'Making your wallet. A few seconds.' : 'One moment.'} />
    );
  }
  if (step === 'pin') {
    return (
      <PinPad
        inline
        title={active === undefined ? 'Your PIN' : active.label}
        hint="Your PIN opens this wallet and signs you in."
        busy={busy}
        onPin={unlock}
        below={
          <button
            className="ghost"
            onClick={() => {
              setWallets(listWallets());
              setStep('choose');
            }}
            data-testid="other-wallet"
          >
            {wallets.length > 1 ? 'Another wallet' : 'Add another wallet'}
          </button>
        }
      />
    );
  }
  if (step === 'choose') {
    return (
      <div className="screen" data-testid="choose-wallet">
        <h2 className="title">Wallets on this device</h2>
        <p className="hint">Each one has its own PIN and its own twelve words.</p>
        <div className="list">
          {wallets.map((w) => (
            <button
              className="row"
              key={w.id}
              onClick={() => {
                if (switchWallet(w.id)) {
                  track('wallet_switch');
                  setStep('pin');
                }
              }}
              data-testid={`wallet-${w.id}`}
            >
              <div>
                <div className="desc">{w.label}</div>
                <div className="meta">
                  {w.solana === ''
                    ? 'Made before wallets were listed'
                    : `${w.solana.slice(0, 6)}…${w.solana.slice(-6)}`}
                  {w.id === activeWalletId() ? ' · open now' : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
        <button
          className="primary"
          onClick={() => {
            startAnotherWallet();
            track('wallet_add');
            setStep('welcome');
          }}
          data-testid="add-wallet"
        >
          Add another wallet
        </button>
        <button
          className="ghost"
          onClick={() => {
            setStep('pin');
          }}
        >
          Back
        </button>
      </div>
    );
  }
  if (step === 'welcome') {
    return (
      <Welcome
        onStart={(from) => {
          track('wallet_start', { from });
          setPinNote(null);
          setStep('new-pin');
        }}
        onRestore={() => {
          track('restore_start');
          setStep('restore');
        }}
      />
    );
  }
  if (step === 'restore') {
    return (
      <div className="screen center">
        <h2 className="title">Your recovery phrase</h2>
        <p className="hint">
          Your twelve words, in order, one in each box. The same words open the same wallet.
        </p>
        <p className="small">
          Only app.xchangehq.com ever asks for these words. Nowhere else, and never in a message.
        </p>
        <RestoreWords
          initial={restorePhrase}
          onPhrase={(p) => {
            setRestorePhrase(p);
            setStep('new-pin');
          }}
        />
        <button
          className="ghost"
          onClick={() => {
            setRestorePhrase('');
            setStep('welcome');
          }}
        >
          Back
        </button>
      </div>
    );
  }
  if (step === 'new-pin') {
    const fresh = restorePhrase === '';
    return (
      <PinPad
        inline
        step={fresh ? 'Step 1 of 3' : undefined}
        title="Choose a PIN"
        hint={pinNote ?? 'Six digits. They stay on this device.'}
        onPin={(pin) => {
          track('pin_first', { fresh });
          setFirstPin(pin);
          setPinNote(null);
          setStep('confirm');
        }}
        onBack={() => {
          setPinNote(null);
          setStep(fresh ? 'welcome' : 'restore');
        }}
      />
    );
  }
  return (
    <PinPad
      inline
      step={restorePhrase === '' ? 'Step 1 of 3' : undefined}
      title="Type it once more"
      hint="The same six digits, to be sure."
      onPin={(pin) => {
        if (pin === firstPin) {
          setFirstPin(null);
          void create(pin);
        } else {
          track('pin_mismatch');
          setFirstPin(null);
          setPinNote('Those two were different. Choose a PIN again.');
          setStep('new-pin');
        }
      }}
      onBack={() => {
        setFirstPin(null);
        setStep('new-pin');
      }}
    />
  );
}

/**
 * The first screen a newcomer sees in a browser, most often a minute after a
 * video. It works before anything is asked: the Simple ticket, on the live
 * book, answers what a trade would come to, and its one button makes the
 * wallet with that ticket kept for afterwards. Over it, a headline for the
 * pair it is on; under it, the book it answers from, then what the minute of
 * making a wallet holds, so the twelve words are known before they arrive.
 */
function Welcome({
  onStart,
  onRestore,
}: {
  onStart: (from: 'welcome' | 'ticket') => void;
  onRestore: () => void;
}) {
  const watch = useWelcomeWatch('web');
  const line = useSharedLine();
  const { market, failed } = useWelcomeMarket();
  // A ticket left here before (Back from the PIN, or another day) opens as it was.
  const [draft] = useState(() => readDraft());
  const [pairName, setPairName] = useState<string | null>(draft?.pair ?? null);
  const pairs = market?.pairs ?? [];
  const pair = pairs.find((p) => p.name === pairName) ?? pairs[0];
  // Until the book arrives, the headline is for the pair the ticket will open on.
  const headline = headlineFor(
    pair?.name ?? (failed || market !== null ? null : (pairName ?? 'XGE/USDC')),
  );
  return (
    <div className="welcome try" data-testid="web-welcome" onPointerDown={watch.tapped}>
      <div className="welcome-pitch welcome-head">
        <p className="welcome-eyebrow">A decentralised peer-to-peer exchange</p>
        <h1 className="welcome-title" data-testid="welcome-title">
          {headline}
        </h1>
        <p className="welcome-lede">A PIN and twelve words. That's the whole sign-up.</p>
      </div>
      <div className="wt-simple welcome-trade">
        {pair === undefined ? (
          // No book to answer from (still reading, or it cannot be read): the
          // wallet is still one press away.
          <div className="wt-card welcome-ticket-wait" data-testid="welcome-ticket-wait">
            <p className="wt-note">
              {failed || market !== null
                ? 'The book cannot be read right now. You can still make your wallet.'
                : 'Reading the book.'}
            </p>
            <button
              className="primary welcome-cta"
              onClick={() => {
                onStart('welcome');
              }}
              data-testid="create-wallet"
            >
              Make a wallet
            </button>
            <p className="welcome-time">About a minute. Nothing to sign up for.</p>
          </div>
        ) : (
          <WelcomeTicket
            pairs={pairs}
            pair={pair}
            onPair={setPairName}
            draft={draft !== null && draft.pair === pair.name ? draft : null}
            onStart={() => {
              onStart('ticket');
            }}
          />
        )}
        <p className="welcome-keys" data-testid="welcome-keys">
          <KeyMark />
          Your keys are made on this device. xchange never has them.
        </p>
        {pair !== undefined && <WelcomeBook pair={pair} />}
      </div>
      <div className="welcome-pitch welcome-rest">
        <SharedLine line={line} className="welcome-shared" />
        <ol className="welcome-steps" aria-label="The minute, step by step">
          <li>
            <span>
              <strong>Pick a PIN.</strong>
            </span>
          </li>
          <li>
            <span>
              <strong>Write twelve words on paper.</strong>
            </span>
          </li>
          <li>
            <span>
              <strong>Swap.</strong> Every swap earns XGE.
            </span>
          </li>
        </ol>
        {/* One line, no button: the card route is met properly inside the wallet. */}
        <p className="welcome-buy small" data-testid="welcome-buy-usdc">
          {BUY_USDC_WELCOME_LINE}
        </p>
        <button className="ghost welcome-restore" onClick={onRestore} data-testid="have-phrase">
          I have a recovery phrase
        </button>
      </div>
    </div>
  );
}

/** A small key, in teal, beside the line that says where the keys are. */
function KeyMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15 19 4M17 6l2 2M14.5 8.5l2 2" />
    </svg>
  );
}
